import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Crown, Trophy, Flag, Sparkles } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const CHAPTERS = [
  {
    icon: Sparkles,
    title: "The Shy Beginning",
    text: "When nine-year-old Taufiq first came to D'La Valentina, he was deeply introverted — the very thought of a stage filled him with fear. Valentina ma'am saw past the shyness: a quiet boy with the bearing of a prince.",
  },
  {
    icon: Crown,
    title: "The Grooming",
    text: "Week after week, the house worked on what no crown can hide — posture, walk, voice, and above all, confidence. The fearful boy learned to own the ramp, the spotlight and the applause.",
  },
  {
    icon: Trophy,
    title: "The Crown in Tbilisi",
    text: "At the international pageant in Tbilisi, Georgia, Taufiq walked for India — and conquered. He returned home as Top Child Model of the Planet India 2019, with the Grand Prix plaque, the trophy and the tricolour held high.",
  },
];

const PHOTOS = [
  { src: "/brand/taufiq-hero.jpg", caption: "The crown and the Grand Prix — Top Teen Model of the Planet" },
  { src: "/brand/taufiq-valentina.jpg", caption: "With Valentina ma'am, the tricolour in hand" },
  { src: "/brand/taufiq-sash.jpg", caption: "Top Child Model of the Planet — India 2019" },
  { src: "/brand/taufiq-beach.jpg", caption: "Cultural presentation on the shores of Georgia" },
  { src: "/brand/taufiq-team.jpg", caption: "With the international contingent in Tbilisi" },
];

export default function WinnerSpotlight() {
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div data-testid="winner-spotlight-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
          <Link to="/" data-testid="spotlight-back-home" className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-neutral-300 transition-colors hover:text-[#D4AF37]">
            <ArrowLeft className="h-4 w-4" strokeWidth={1} /> Back to the House
          </Link>
          <span className="font-script text-2xl text-[#D4AF37]">D'La Valentina</span>
        </div>
      </header>

      <section className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:py-28">
        <Reveal>
          <div className="flex items-center gap-3">
            <Trophy className="h-6 w-6 text-[#D4AF37]" strokeWidth={1} />
            <span className="font-script text-3xl text-[#D4AF37]">Winner Spotlight</span>
          </div>
          <h1 data-testid="spotlight-name" className="font-display mt-5 text-5xl font-medium leading-tight md:text-6xl">
            Taufiq Mansoor Ansari
          </h1>
          <p data-testid="spotlight-title" className="mt-5 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]/80">
            Top Child Model of the Planet — India 2019
          </p>
          <p className="mt-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-neutral-400">
            <Flag className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={1.5} /> International Pageant · Tbilisi, Georgia
          </p>
          <blockquote className="font-display mt-10 border-l-2 border-[#D4AF37]/50 pl-8 text-2xl font-light italic leading-relaxed text-neutral-200">
            “It was only because of Valentina ma’am that I was able to win that title.”
          </blockquote>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="clipped-frame overflow-hidden">
            <img
              src="/brand/taufiq-hero.jpg"
              alt="Taufiq Mansoor Ansari with his crown, trophy and Grand Prix plaque"
              data-testid="spotlight-hero-image"
              className="max-h-[640px] w-full bg-black object-contain"
            />
          </div>
        </Reveal>
      </section>

      <section className="border-y border-white/5 bg-[#0A0A0A]">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal className="mb-16 text-center">
            <span className="font-script text-3xl text-[#D4AF37]">His Journey</span>
            <h2 className="font-display mt-3 text-4xl font-medium md:text-5xl">From Stage Fear to World Stage</h2>
          </Reveal>
          <div className="grid gap-10 md:grid-cols-3">
            {CHAPTERS.map((ch, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div data-testid={`spotlight-chapter-${i}`} className="h-full border border-white/10 bg-[#0C0C0C] p-8 transition-colors duration-500 hover:border-[#D4AF37]/50">
                  <div className="flex items-center justify-between">
                    <ch.icon className="h-6 w-6 text-[#D4AF37]" strokeWidth={1} />
                    <span className="font-display text-3xl font-light text-[#D4AF37]/30">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-display mt-6 text-2xl font-medium text-white">{ch.title}</h3>
                  <p className="mt-4 text-sm font-light leading-relaxed text-neutral-400">{ch.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-24">
        <Reveal className="mb-16 text-center">
          <span className="font-script text-3xl text-[#D4AF37]">Moments of Glory</span>
          <h2 className="font-display mt-3 text-4xl font-medium md:text-5xl">The Tbilisi Album</h2>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PHOTOS.map((p, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08}>
              <figure data-testid={`spotlight-photo-${i}`} className="group border border-white/10 bg-black transition-colors duration-500 hover:border-[#D4AF37]/50">
                <img src={p.src} alt={p.caption} loading="lazy" className="h-80 w-full object-contain transition-transform duration-700 group-hover:scale-105" />
                <figcaption className="border-t border-white/10 px-5 py-4 text-center text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                  {p.caption}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-[#D4AF37]/25 bg-gradient-to-b from-[#0C0C0C] to-[#050505]">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <Reveal>
            <Crown className="mx-auto h-10 w-10 text-[#D4AF37]" strokeWidth={1} />
            <h2 className="font-display mt-6 text-4xl font-medium md:text-5xl">Your Child Could Be the Next Story</h2>
            <p className="mx-auto mt-6 max-w-xl text-sm font-light leading-loose text-neutral-300">
              Taufiq arrived as a shy nine-year-old and returned from Tbilisi a champion of India.
              The Little Stars category begins at age 3 — every reign begins with one brave step.
            </p>
            <a
              href="/register"
              data-testid="spotlight-cta-button"
              className="mt-10 inline-flex items-center gap-3 bg-[#D4AF37] px-10 py-4 text-xs font-medium uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-[#F9F1CC]"
            >
              Begin Their Reign
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
